import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ProgressReportApiService } from '../../../core/api-services/progress-report-api.service';
import { ProgressReport } from '../../../core/models/progress-report';

/**
 * Student academic progress reports.
 *
 * One series per scholarship award (and therefore per cycle): the student
 * reports on their academic progress and attaches the academic record. A report
 * sent back for resubmission is answered with a fresh report.
 */
@Component({
  selector: 'app-student-progress-reports',
  standalone: true,
  imports: [CommonModule, DatePipe, ReactiveFormsModule],
  templateUrl: './progress-reports.html',
  styleUrl: './progress-reports.scss',
})
export class StudentProgressReportsComponent implements OnInit {
  private readonly api = inject(ProgressReportApiService);
  private readonly fb = inject(FormBuilder);

  readonly reports = signal<ProgressReport[]>([]);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly uploading = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly showForm = signal(false);

  /** The academic record uploaded for the report being submitted. */
  readonly document = signal<{ objectKey: string; fileName: string } | null>(null);

  readonly form = this.fb.group({
    title: ['', Validators.required],
    academicPeriod: [''],
    institutionName: [''],
    averageScore: [null as number | null],
    summary: [''],
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');

    this.api
      .getMyReports()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (reports) => this.reports.set(reports ?? []),
        error: () => this.error.set('Your progress reports could not be loaded.'),
      });
  }

  openForm(): void {
    this.error.set('');
    this.message.set('');
    this.document.set(null);
    this.form.reset({ title: '', academicPeriod: '', institutionName: '', averageScore: null, summary: '' });
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.uploading.set(true);
    this.error.set('');

    this.api
      .uploadDocument(file)
      .pipe(finalize(() => this.uploading.set(false)))
      .subscribe({
        next: (uploaded) => {
          this.document.set({ objectKey: uploaded.objectKey, fileName: uploaded.fileName });
          input.value = '';
        },
        error: (err) => this.error.set(err?.error?.message || 'The document could not be uploaded.'),
      });
  }

  submit(): void {
    if (this.form.invalid || this.busy()) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .submit({
        title: value.title ?? '',
        academicPeriod: value.academicPeriod || null,
        institutionName: value.institutionName || null,
        averageScore: value.averageScore ?? null,
        summary: value.summary || null,
        documentObjectKey: this.document()?.objectKey ?? null,
        documentFileName: this.document()?.fileName ?? null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (report) => {
          this.message.set(`Progress report ${report.referenceNumber} submitted for review.`);
          this.showForm.set(false);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The progress report could not be submitted.'),
      });
  }

  download(report: ProgressReport): void {
    this.api.downloadDocument(report.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = report.documentFileName || `progress-report-${report.referenceNumber}`;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.error.set('The academic record could not be downloaded.'),
    });
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'RESUBMIT':
        return 'status status-booked';
      default:
        return 'status status-pending';
    }
  }
}
